import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from 'lodash';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import * as requestFromServer from "../Redux/mainRedux/mainCrud";

const List = ({
  titles,
  initialCollection = [],
  textAlign = 'left',
  outlineClass,
  searchLoading,
  isSearchable,
  onRowClick,
  onDetailClick
}) => {
  // Redux state
  const {
    baseAPIURL,
    sessionStorage,
    token
  } = useSelector(state => state.slice);

  // Redux dispatcher
  const dispatch = useDispatch();

  // Local state
  const [collection, setCollection] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  // Local vars
  const listRef = useRef(null);
  const pageLimit = 10;

  // Initial page load of collection
  useEffect(() => {
    handleSearch(searchValue);
  }, [initialCollection, searchLoading]);

  // Handle paging/loading more records based on scrolling
  useLayoutEffect(() => {
    const list = listRef.current;
    const lastPage = (collection.length > 0 && (collection.length % pageLimit > 0));

    const handleScrollPagination = (e) => {
      const visibleHeight = list.offsetHeight;
      const totalHeight = list.scrollHeight;
      let totalScrolled = Math.round(list.scrollTop + visibleHeight);
      let isScrollEnd = false;

      if (totalScrolled < totalHeight) {
        isScrollEnd = totalScrolled + 1 >= totalHeight;
      } else {
        isScrollEnd = totalScrolled >= totalHeight;
      }

      if (isScrollEnd && ! lastPage) {
        const nextPage = searchLoading ? Math.floor(collection.length / pageLimit) + 1 : null;
        handleSearch(searchValue, nextPage);
      }
    };

    list.addEventListener('scroll', handleScrollPagination);

    return () => list.removeEventListener('scroll', handleScrollPagination);
  }, [collection, searchLoading]);

  // Handle search value change
  const handleSearchValueChange = (e) => {
    const v = e?.target?.value;
    setSearchValue(v);
    debounceSearch(v, 1);
  };

  // Handle a search or request for next page of data
  const handleSearch = (v = "", p = null) => {
    // Is searching enabled?
    if (! isSearchable) {
      setCollection(initialCollection);
      return;
    }

    // Set search loading
    setIsSearchLoading(true);

    // Handle local search vs API search request
    if (! searchLoading) {
      // Local search
      if (v) {
        const filteredCollection = initialCollection.filter(row => {
          return (row?.value?.toString().toLowerCase().indexOf(v) !== -1);
         });
        setCollection(filteredCollection);
      } else {
        setCollection(initialCollection);
      }
      setIsSearchLoading(false);
    } else {
      // API search
      let page = p;
      let body = {
        apiSessionStorage: sessionStorage,
        is_json: true,
        ...searchLoading?.call_back_url_params,
        page: page,
        value: v
      };
      requestFromServer.Call_API(baseAPIURL, searchLoading?.callback_url, body, 'post', token)
        .then(response => {
          const newValues = response?.data?.data?.search_object?.value || response?.data?.data?.attributes?.search_object?.value || [];
          if (page > 1) {
            setCollection([...collection, ...newValues]);
          } else {
            setCollection(newValues);
          }
          setIsSearchLoading(false);
        })
        .catch(error => {
          setCollection([]);
          setIsSearchLoading(false);
        });
    }
  };

  // Debounce for search request
  const debounceSearch = useCallback(
    debounce((v, p = null) => {
      handleSearch(v, p);
    }, 500)
  , [baseAPIURL, collection, initialCollection, searchLoading, sessionStorage, token]);

  // Stop debounced requests when unmounting
  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    }
  }, [debounceSearch]);

  return (
    <>
      <div
        className="col-sm-12 col-xs-12 col-12"
        style={{ height: 'calc(100vh - 130px)', overflowY: 'auto' }}
        ref={listRef}
      >
        {isSearchable && (
          <div className="col-12 cpt-10 cpb-10 cpr-10 cpl-10 ScanScreen1Header search_holder align-items-center">
            <img className="floatleft" src={process.env.PUBLIC_URL + '/Assets/icons/search.png'} alt="line" />
            <input
              type="text"
              className="font-family-inherit backgroundColor-Type4 search_input cpl-10"
              placeholder="Search"
              value={searchValue}
              onChange={handleSearchValueChange}
            />
          </div>
        )}

        <div className="Inquiry cpt-10 listScroll">
          {titles && (
            <div className={`title font-large ${textAlign}`}>
              {
                titles.map((title, index) => {
                  return (
                    <div
                      key={index}
                      className={`tableColumn cpt-15 cpb-15 cpl-15 cpr-15 ${titles.length === 1 ? 'text-center' : ''}`}
                    >
                      {title}
                    </div>
                  )
                })
              }
            </div>
          )}

          {collection && collection.map((row, index) => {
            return (
              <div
                key={index}
                className={`detail bg-white font-small2 cmb-8 mx-2 ${textAlign} ${index == 0 ? `${outlineClass}` : ''} `}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {Object.keys(row).filter(key => {
                  return (
                    key !== 'callback_url' &&
                    key !== 'call_back_url_params' &&
                    key !== 'info');
                }).map((key, index) => {
                  return (
                    <div
                      key={index}
                      className={
                        `tableColumn cpt-15 cpb-15 cpl-15 cpr-15 ${Object.keys(row).length === 1 ? 'text-center' : ''}`
                      }
                    >
                      {row[key]}
                    </div>
                  )
                })}
                {onDetailClick && row?.info && (
                  <div
                    key={row.length}
                    className="tableColumn cpt-15 cpb-15 cpl-15 cpr-15"
                  >
                    <img
                      className="floatRight"
                      src={process.env.PUBLIC_URL + '/Assets/icons/detail.png'}
                      alt="detail" onClick={(e) => {
                        e.stopPropagation();
                        onDetailClick(row);
                      }}
                    />      
                  </div>
                )}
              </div>
            )
          })}

        </div>
      </div>
			{isSearchLoading && (
				<div className="mt-2">
					<Box sx={{ display: 'flex', justifyContent: 'center' }}>
						<CircularProgress size={20} thickness={4} />
					</Box>
				</div>
			)}
    </>
  );
};

export default List;
